/**
 * Вызовы Chatwoot для раздела «Входящие». Каждый адрес сверен с config/routes.rb нашей копии
 * (_vendor/chatwoot) и с контроллером — см. комментарии у функций.
 * Сессия, ротация пропуска и повтор на 401 — в cwFetch (src/api/chatwoot.ts).
 */
import { cwFetch } from './chatwoot';
import {
  normalizeConversation, type CwInboxFull, type CwInboxStage, type CwMessage, type CwUnreadCounts,
  type InboxConversation, type RawConversation,
} from './inboxTypes';
import { ApiError } from '@/lib/http';
import { toSearchParams, totalFromMeta, type ListQuery } from '@/lib/inboxQuery';
import type { ListPage } from '@/lib/convList';
import type { ChannelCapabilities } from '@/lib/channelRules';

const base = (accountId: number) => `/api/v1/accounts/${accountId}`;

export interface CwProfileFull {
  id: number;
  name: string;
  /** Ключ подписки на события (RoomChannel). */
  pubsub_token: string;
  accounts: Array<{ id: number; name: string }>;
}

/** GET /api/v1/profile (profiles/show.json.jbuilder) — id пользователя и pubsub_token. */
export const getProfileFull = () => cwFetch<CwProfileFull>('/api/v1/profile');

interface ListResponse {
  data: {
    meta: { mine_count: number; assigned_count: number; unassigned_count: number; all_count: number };
    payload: RawConversation[];
  };
}

/**
 * GET /conversations — conversations_controller#index → ConversationFinder#perform.
 * Ответ { data: { meta, payload } } (accounts/conversations/index.json.jbuilder).
 */
export async function listConversations(accountId: number, query: ListQuery, page: number): Promise<ListPage> {
  const r = await cwFetch<ListResponse>(`${base(accountId)}/conversations?${toSearchParams(query, page)}`);
  return {
    conversations: r.data.payload.map((c) => normalizeConversation(c, accountId)),
    total: totalFromMeta(r.data.meta, query),
  };
}

/** GET /conversations/meta — те же фильтры, только счётчики (число «ждут ответа» в подзаголовке). */
export async function getListMeta(accountId: number, query: ListQuery) {
  const r = await cwFetch<{ meta: { mine_count: number; assigned_count: number; unassigned_count: number; all_count: number } }>(
    `${base(accountId)}/conversations/meta?${toSearchParams(query)}`,
  );
  return r.meta;
}

/** GET /conversations/:display_id (show; в маршрутах — по display_id внутри кабинета). */
export async function getConversation(accountId: number, displayId: number): Promise<InboxConversation> {
  const raw = await cwFetch<RawConversation>(`${base(accountId)}/conversations/${displayId}`);
  return normalizeConversation(raw, accountId);
}

/** GET /inboxes → { payload: [...] } (видны только доступные человеку). */
export async function listInboxes(accountId: number): Promise<CwInboxFull[]> {
  const r = await cwFetch<{ payload: CwInboxFull[] }>(`${base(accountId)}/inboxes`);
  return r.payload;
}

/** GET /inboxes/:id/stages — наше (inboxes_controller#stages): голый массив, по возрастанию position. */
export const listInboxStages = (accountId: number, inboxId: number) =>
  cwFetch<CwInboxStage[]>(`${base(accountId)}/inboxes/${inboxId}/stages`);

/**
 * GET /conversations/unread_counts → { payload: { all_count, inboxes, labels, teams, stages } }.
 * 403 — на кабинете выключена функция conversation_unread_counts: тогда счётчиков нет (null).
 * Считает только open-переписки и до 10 непрочитанных на переписку.
 */
export async function getUnreadCounts(accountId: number): Promise<CwUnreadCounts | null> {
  try {
    const r = await cwFetch<{ payload: CwUnreadCounts }>(`${base(accountId)}/conversations/unread_counts`);
    return r.payload;
  } catch (e) {
    if (e instanceof ApiError && e.status === 403) return null;
    throw e;
  }
}

export interface MessagesResponse {
  meta?: Record<string, unknown>;
  payload: CwMessage[];
}

/**
 * GET /conversations/:id/messages — message_finder.rb: без параметров 20 последних,
 * before=<id> — 20 перед ним, after=<id> — до 100 после. Ответ по возрастанию времени.
 */
export async function listMessages(accountId: number, displayId: number, before?: number): Promise<CwMessage[]> {
  const q = before !== undefined ? `?before=${before}` : '';
  const r = await cwFetch<MessagesResponse>(`${base(accountId)}/conversations/${displayId}/messages${q}`);
  return r.payload;
}

export interface SendPayload {
  content: string;
  isPrivate: boolean;
  echoId: string;
}

/**
 * POST /conversations/:id/messages (messages_controller#create → Messages::MessageBuilder):
 * content, private, echo_id, message_type=outgoing. echo_id возвращается в ответе и в событии
 * message.created — по нему склеиваем оптимистичное сообщение.
 */
export const sendMessage = (accountId: number, displayId: number, p: SendPayload) =>
  cwFetch<CwMessage>(`${base(accountId)}/conversations/${displayId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content: p.content, private: p.isPrivate, echo_id: p.echoId, message_type: 'outgoing' }),
  });

/**
 * Отправка с файлами: тот же адрес, тело — multipart (attachments[], content, private, echo_id,
 * is_voice_message). Собирает форму lib/attachments.ts. Ждём дольше обычного: 40 МБ по мобильной сети.
 */
export const sendMessageForm = (accountId: number, displayId: number, form: FormData) =>
  cwFetch<CwMessage>(`${base(accountId)}/conversations/${displayId}/messages`, { method: 'POST', body: form }, true, 180_000);

/** POST /conversations/:id/update_last_seen — отметить прочитанным (ответ может быть пустым). */
export const markConversationRead = (accountId: number, displayId: number) =>
  cwFetch<unknown>(`${base(accountId)}/conversations/${displayId}/update_last_seen`, { method: 'POST' });

/**
 * GET /inboxes/:id/unia_capabilities (наш UniaCapabilitiesController) — правила канала для строки ввода:
 * голосовые или только текст, виды файлов, по одному ли, подпись, предел. `conversation_id` (display_id) нужен
 * странице Facebook: её переписки Instagram Direct живут по правилам Instagram.
 * Старый сервер без этого адреса (404) → null: строка ввода ведёт себя как раньше.
 */
export async function getInboxCapabilities(
  accountId: number, inboxId: number, conversationId?: number,
): Promise<ChannelCapabilities | null> {
  const q = conversationId ? `?conversation_id=${conversationId}` : '';
  try {
    return await cwFetch<ChannelCapabilities>(`${base(accountId)}/inboxes/${inboxId}/unia_capabilities${q}`);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

/**
 * GET /conversations/search?q= (conversations_controller#search): поиск переписок по имени, телефону, почте и тексту.
 * Ответ { meta, payload: [...] }; на случай обёртки как у списка — читаем и { data: { payload } }.
 */
export async function searchConversations(accountId: number, text: string): Promise<InboxConversation[]> {
  const r = await cwFetch<{ payload?: RawConversation[]; data?: { payload?: RawConversation[] } }>(
    `${base(accountId)}/conversations/search?q=${encodeURIComponent(text)}`,
  );
  return (r.payload ?? r.data?.payload ?? []).map((c) => normalizeConversation(c, accountId));
}
